<template>
  <WidgetWrapper ref="widget" :style="widgetStyle">
    <div v-if="!activeLoading" class="tabs-wrapper mb-3">
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'similarSites' }"
        @click="switchTab('similarSites')"
      >
        {{ $t('main.similarSites.tabs.similarSites') }}
      </button>
      <button
        class="tab-btn"
        :class="{ active: activeTab === 'subDomains' }"
        @click="switchTab('subDomains')"
      >
        {{ $t('main.similarSites.tabs.subDomains') }}
      </button>
    </div>
    <WidgetTitle
      v-if="!activeLoading"
      :title="activeTitle"
      :description="activeDescription"
    />

    <!-- Similar Sites content -->
    <div
      v-if="activeTab === 'similarSites' && isSimilarData"
      class="content d-flex flex-column"
    >
      <div class="d-flex flex-column">
        <div class="row row-cols-1 justify-content-between">
          <WidgetListTitle
            class="plr-15 mb-2"
            :firstName="$t('main.similarSites.widgetListTitle')"
            :secondName="$t('main.similarSites.widgetListValue')"
          />
        </div>
        <div class="row row-cols-1 justify-content-between">
          <button
            class="d-flex justify-content-between wrapper_domain_content"
            v-for="(item, index) of dataOnActualDateLocal.data.slice(
              0,
              competitorsCount
            )"
            :key="index"
            @click="showDialog(item.domainName, index)"
            @focusout="dialog.show = false"
            @mouseover="mouseOverIndex = index"
            @mouseleave="mouseOverIndex = null"
          >
            <div>
              <Domain
                class="domain"
                :domain="item.domainName"
                :isLink="isMobile() ? false : true"
              />
              <img
                :src="CheckmarkIcon"
                v-if="domains.includes(item.domainName)"
                class="ms-2"
              />
              <CompareButton
                v-else
                v-show="mouseOverIndex === index"
                :domain="item.domainName"
              />
            </div>
            <div class="wrapper_link_after d-flex h-100">
              <div class="visits ms-2">
                <template v-if="item.lastMonthVisits >= 0">
                  {{ nFormatter(item.lastMonthVisits) }}
                </template>
                <template v-else>
                  &lt;50K
                </template>
              </div>
            </div>
            <DomainContextMenu
              class="d-lg-none"
              :domain="item.domainName"
              v-if="dialog.show && dialog.index === index"
            />
          </button>
        </div>
      </div>
      <ShowMoreButton
        v-if="totalCount > 16"
        class="mt-20"
        @clickCustom="onModalOpen()"
        :totalCount="totalCount"
      />
    </div>

    <!-- Subdomains content -->
    <div
      v-if="activeTab === 'subDomains' && isSubDomainsData"
      class="content d-flex flex-column"
    >
      <div class="d-flex flex-column">
        <div class="row row-cols-1 justify-content-between">
          <WidgetListTitle
            class="plr-15 mb-2"
            :firstName="$t('main.similarSites.subDomains.widgetListTitle')"
            :secondName="$t('main.similarSites.subDomains.widgetListValue')"
          />
        </div>
        <div
          v-for="group of subDomainsData"
          :key="group.domainName"
          class="subdomain-group"
        >
          <div class="subdomain-parent plr-15 mb-1">
            <Domain class="domain" :domain="group.domainName" :isLink="true" />
          </div>
          <div class="row row-cols-1 justify-content-between">
            <button
              class="d-flex justify-content-between wrapper_domain_content subdomain-item"
              v-for="(item, index) of group.data.slice(0, competitorsCount)"
              :key="index"
              @click="showDialog(item.domainName, `sub-${group.domainName}-${index}`)"
              @focusout="dialog.show = false"
              @mouseover="mouseOverIndex = `sub-${group.domainName}-${index}`"
              @mouseleave="mouseOverIndex = null"
            >
              <div>
                <Domain
                  class="domain"
                  :domain="item.domainName"
                  :isLink="isMobile() ? false : true"
                />
                <img
                  :src="CheckmarkIcon"
                  v-if="domains.includes(item.domainName)"
                  class="ms-2"
                />
                <CompareButton
                  v-else
                  v-show="mouseOverIndex === `sub-${group.domainName}-${index}`"
                  :domain="item.domainName"
                />
              </div>
              <div class="wrapper_link_after d-flex h-100">
                <div class="visits ms-2">
                  <template v-if="item.lastMonthVisits >= 0">
                    {{ nFormatter(item.lastMonthVisits) }}
                  </template>
                  <template v-else>
                    &lt;50K
                  </template>
                </div>
              </div>
              <DomainContextMenu
                class="d-lg-none"
                :domain="item.domainName"
                v-if="dialog.show && dialog.index === `sub-${group.domainName}-${index}`"
              />
            </button>
          </div>
        </div>
      </div>
    </div>

    <Modal
      type="table"
      :closeHandler="close => (isModalOpen = false)"
      :isOpen="isModalOpen"
    >
      <template v-slot:title>
        {{ $t('main.similarSites.table.title') }}
      </template>
      <template v-slot:body> <TableSimilarSites /> </template>
    </Modal>
    <WidgetError v-if="!activeHasData && !activeLoading && !subDomainsEmpty" />
    <div v-if="subDomainsEmpty" class="subdomains-empty">
      {{ $t('main.similarSites.subDomains.notFound') }}
    </div>
    <SkeletonLoading v-if="activeLoading" name="similarSites" />
  </WidgetWrapper>
</template>

<script>
import WidgetWrapper from '@/components/widgets/widgets-helpers/WidgetWrapper.vue'
import WidgetTitle from '@/components/widgets/widgets-helpers/WidgetTitle.vue'
import DomainContextMenu from '@/components/shared/DomainContextMenu.vue'
import SkeletonLoading from '@/components/SkeletonLoading.vue'
import WidgetError from '@/components/errors/WidgetError.vue'
import { mapGetters, mapState } from 'vuex'
import ShowMoreButton from '@/components/buttons/ShowMoreButton.vue'
import CompareButton from '@/components/buttons/CompareButton.vue'
import WidgetListTitle from '@/components/widgets/widgets-helpers/WidgetListTitle.vue'
import Domain from '@/components/widgets/widgets-helpers/Domain.vue'
import CheckmarkIcon from '@/assets/icons/base/checkmark.svg'
import isMobile from '@/helpers/base/isMobile'
import Modal from '@/components/modals/Modal.vue'
import TableSimilarSites from '@/components/tables/TableSimilarSites.vue'
import nFormatter from '../../../helpers/processing/nFormatter'

export default {
  data() {
    return {
      activeTab: 'similarSites',
      widgetHeight: null,
      mouseOverIndex: null,
      dialog: {
        show: false,
        domain: null,
        index: null,
      },
      competitorsCount: 16,
      isModalOpen: false,
      CheckmarkIcon,
    }
  },
  components: {
    SkeletonLoading,
    WidgetError,
    WidgetTitle,
    WidgetWrapper,
    ShowMoreButton,
    Domain,
    CompareButton,
    WidgetListTitle,
    DomainContextMenu,
    Modal,
    TableSimilarSites,
  },
  computed: {
    ...mapGetters(['domains']),
    ...mapGetters('similarSites', ['dataOnActualDate']),
    ...mapState('similarSites', {
      similarSites: state => state,
    }),
    ...mapState('subDomains', {
      subDomainsState: state => state,
    }),
    dataOnActualDateLocal() {
      return this.dataOnActualDate[0]
    },
    isSimilarData() {
      return (
        this.dataOnActualDateLocal?.data?.length &&
        !this.similarSites.loading &&
        !this.similarSites.error.status
      )
    },
    isSubDomainsData() {
      return (
        this.subDomainsData.length > 0 &&
        !this.subDomainsState.loading &&
        !this.subDomainsState.error.status
      )
    },
    subDomainsData() {
      return this.subDomainsState.data.filter(g => g.data?.length)
    },
    subDomainsEmpty() {
      return (
        this.activeTab === 'subDomains' &&
        !this.subDomainsState.loading &&
        !this.subDomainsState.error.status &&
        this.subDomainsState.loaded &&
        this.subDomainsData.length === 0
      )
    },
    activeHasData() {
      return this.activeTab === 'similarSites'
        ? this.isSimilarData
        : this.isSubDomainsData
    },
    activeLoading() {
      return this.activeTab === 'similarSites'
        ? this.similarSites.loading
        : this.subDomainsState.loading
    },
    activeTitle() {
      return this.activeTab === 'similarSites'
        ? this.$t('main.similarSites.title')
        : this.$t('main.similarSites.tabs.subDomains')
    },
    activeDescription() {
      return this.activeTab === 'similarSites'
        ? this.$t('main.similarSites.description')
        : this.$t('main.similarSites.subDomains.description')
    },
    totalCount() {
      return this.dataOnActualDateLocal?.totalCount || 0
    },
    widgetStyle() {
      if (this.widgetHeight) {
        return { height: this.widgetHeight + 'px', overflow: 'hidden' }
      }
      return {}
    },
  },
  methods: {
    nFormatter,
    isMobile,
    showDialog(domain, index) {
      this.dialog.show = !this.dialog.show
      this.dialog.domain = domain
      this.dialog.index = index
    },
    onModalOpen() {
      this.isModalOpen = true
    },
    switchTab(tab) {
      if (this.$refs.widget?.$el) {
        this.widgetHeight = this.$refs.widget.$el.offsetHeight
      }
      this.activeTab = tab
      if (tab === 'subDomains' && !this.subDomainsState.loaded) {
        this.$store.dispatch('subDomains/load')
      }
    },
  },
}
</script>

<style scoped>
.wrapper {
  position: relative;
}

@media (max-width: 1476px) {
  .wrapper_domain_content:nth-child(1n + 11) {
    display: none !important;
  }
}

@media (max-width: 992px) {
  .wrapper_domain_content:nth-child(1n + 8) {
    display: none !important;
  }
}

.domain {
  max-width: 240px;
}

.plr-15 {
  padding-left: 15px;
  padding-right: 15px;
}
.mt-20 {
  margin-top: 26px;
}

.wrapper_domain_content {
  padding: 7px 15px 6px;
  position: relative;
  background: none;
}
.wrapper_domain_content:hover {
  background: var(--bg-hover);
  border-radius: 8px;
}

img .path {
  fill: 'red';
}

.topics {
  font-size: 12px;
  color: var(--text-secondary3);
}

.visits {
  font-weight: 600;
  font-size: 14px;
  line-height: 19px;
  color: var(--text-primary);
}

.add_icon {
  stroke: var(--main-color);
  width: 9px;
  height: 9px;
}

.success_icon {
  fill: var(--success-color-2);
}

.wrapper_success {
  display: inline-block;
}

.tabs-wrapper {
  display: flex;
  gap: 0;
  border-bottom: 1px solid var(--border-color);
}

.tab-btn {
  background: none;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: var(--text-secondary3);
  cursor: pointer;
  border-bottom: 2px solid transparent;
  transition: color 0.2s, border-color 0.2s;
}

.tab-btn:hover {
  color: var(--text-primary);
}

.tab-btn.active {
  color: var(--text-primary);
  border-bottom-color: var(--main-color);
}

.subdomain-group {
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1.5px solid var(--border-color);
}

.subdomain-group:last-child {
  border-bottom: none;
  margin-bottom: 0;
  padding-bottom: 0;
}

.subdomain-parent {
  font-weight: 600;
  font-size: 13px;
  color: var(--text-secondary3);
  padding-top: 6px;
}

.subdomain-item {
  padding-left: 15px !important;
}

.subdomains-empty {
  color: var(--text-secondary3);
  text-align: center;
  margin: 10px 0;
}
</style>
